/**
 * After the code: what a passkey means for THIS person, in their terms.
 *  - required: their company signs in with passkeys — creating one is how they get in (or using one they have);
 *  - recommended: they are in, and a passkey unlocks what a code cannot (managing people, a passkey-only company);
 *  - optional: they are in; a passkey only spares the next code. Offered once per device, never on a computer
 *    that cannot hold one.
 */

import { Icon, Notice, Spinner } from '@evt/flow-stream-ui'
import type { PasskeyAdvice } from '@portal/contracts'
import { supportsPasskeys } from '../../auth/passkey'
import { listNames } from '../../auth/sign-in-flow'
import { useI18n } from '../../i18n'

export function PasskeyStep({
  mode,
  advice,
  busy,
  onCreate,
  onUseExisting,
  onNotNow
}: {
  mode: 'required' | 'recommended' | 'optional'
  advice: PasskeyAdvice
  busy: boolean
  onCreate: () => void
  onUseExisting: () => void
  onNotNow: () => void
}) {
  const { t } = useI18n()
  const companies = listNames(advice.passkeyOnlyCompanies, t.login.and)
  const canCreate = supportsPasskeys()
  const reasons = [
    ...(advice.admin ? [t.login.passkeyAdminReason] : []),
    ...(companies ? [t.login.passkeyCompaniesReason(companies)] : [])
  ]

  return (
    <div className="login-body">
      {mode === 'required' && (
        <p className="login-lead">
          {companies ? t.login.passkeyRequiredBody(companies) : t.login.passkeyRequiredBodyGeneric}
        </p>
      )}
      {mode === 'recommended' && <Reasons reasons={reasons} />}
      {mode === 'optional' && <p className="login-lead">{t.login.passkeyOptionalBody}</p>}

      {canCreate ? (
        <>
          <button type="button" className="btn btn-primary btn-block" disabled={busy} onClick={onCreate}>
            {busy ? <Spinner size={14} /> : <Icon name="shield" size={15} />} {t.login.createPasskey}
          </button>
          <p className="login-hint">{t.login.passkeyHowTo}</p>
        </>
      ) : (
        <Notice kind="warning">{t.login.passkeyUnsupported}</Notice>
      )}
      {mode === 'required' && advice.hasPasskeys && canCreate && (
        <button type="button" className="btn btn-block" disabled={busy} onClick={onUseExisting}>
          {t.login.useExistingPasskey}
        </button>
      )}
      {mode !== 'required' && (
        <button type="button" className="btn btn-ghost btn-block" disabled={busy} onClick={onNotNow}>
          {t.login.notNow}
        </button>
      )}
    </div>
  )
}

/** One reason reads as a sentence; two as a short list. */
function Reasons({ reasons }: { reasons: string[] }) {
  if (reasons.length === 1) return <p className="login-lead">{reasons[0]}</p>
  return (
    <ul className="login-reasons">
      {reasons.map((r) => (
        <li key={r}>{r}</li>
      ))}
    </ul>
  )
}
